import type { TCalendarPurpose } from '@/services/calendly';

type TQueryParams = Record<string, unknown>;

const createQueryKeys = <TDomain extends string>(domain: TDomain) => ({
  root: [domain] as const,
  list: (params?: TQueryParams) => [domain, 'list', params ?? {}] as const,
  count: (params?: TQueryParams) => [domain, 'count', params ?? {}] as const,
  detail: (id: string) => [domain, 'detail', id] as const,
});

const STAFF_KEYS = {
  ...createQueryKeys('staff'),
  session: () => ['staff', 'session'] as const,
  profile: () => ['staff', 'profile'] as const,
  directory: () => ['staff', 'directory'] as const,
  factors: () => ['staff', 'factors'] as const,
  permissions: () => ['staff', 'permissions'] as const,
};

const STAFF_INVITE_KEYS = {
  ...createQueryKeys('staff-invites'),
  open: () => ['staff-invites', 'open'] as const,
  registration: () => ['staff-invites', 'registration'] as const,
};

const CALENDLY_KEYS = {
  ...createQueryKeys('calendly'),
  mine: () => ['calendly', 'mine'] as const,
  options: (staffID: string, purpose: TCalendarPurpose) => ['calendly', 'options', staffID, purpose] as const,
};

const AUDIT_KEYS = createQueryKeys('audit');

const APPLICANT_KEYS = {
  ...createQueryKeys('applicants'),
  photos: (id: string) => ['applicants', 'photos', id] as const,
};

const SINGLE_KEYS = {
  ...createQueryKeys('singles'),
  candidates: (params?: TQueryParams) => ['singles', 'candidates', params ?? {}] as const,
  profile: (id: string) => ['singles', 'profile', id] as const,
  book: (params?: TQueryParams) => ['singles', 'book', params ?? {}] as const,
  search: (params?: TQueryParams) => ['singles', 'search', params ?? {}] as const,
  bookCount: (params?: TQueryParams) => ['singles', 'book-count', params ?? {}] as const,
};

const SAFETY_KEYS = {
  ...createQueryKeys('safety'),
  bans: (since: string) => ['safety', 'bans', since] as const,
};

const MEMBER_KEYS = {
  ...createQueryKeys('members'),
  visibility: (memberID: string) => ['members', 'visibility', memberID] as const,
};

const VERIFICATION_KEYS = createQueryKeys('verification');

const CONSENT_KEYS = createQueryKeys('consents');

const MODERATION_KEYS = {
  ...createQueryKeys('moderation'),
  member: (memberID: string) => ['moderation', 'member', memberID] as const,
  live: (contentIDs: readonly string[]) => ['moderation', 'live', contentIDs] as const,
  livePrompts: (promptIDs: readonly string[]) => ['moderation', 'live-prompts', promptIDs] as const,
  bio: (memberID: string) => ['moderation', 'bio', memberID] as const,
};

const PREFERENCE_KEYS = {
  ...createQueryKeys('preferences'),
  ranks: (memberID: string) => ['preferences', 'ranks', memberID] as const,
  changes: (memberID: string) => ['preferences', 'changes', memberID] as const,
  selectivity: (memberID: string) => ['preferences', 'selectivity', memberID] as const,
};

const ACTIVITY_KEYS = {
  ...createQueryKeys('activity'),
  matches: (memberID: string) => ['activity', 'matches', memberID] as const,
  approaches: (memberID: string) => ['activity', 'approaches', memberID] as const,
  passes: (memberID: string) => ['activity', 'passes', memberID] as const,
};

const CHAT_KEYS = {
  ...createQueryKeys('chat'),
  unreadPeers: (staffID: string) => ['chat', 'unread-peers', staffID] as const,
};

const INVITE_KEYS = {
  ...createQueryKeys('invites'),
  register: (id: string) => ['invites', 'register', id] as const,
  submittedAt: (applicantID: string) => ['invites', 'submitted-at', applicantID] as const,
  inviterBalance: (inviterID: string) => ['invites', 'inviter-balance', inviterID] as const,
};

const ONBOARDING_REQUEST_KEYS = {
  ...createQueryKeys('onboarding-requests'),
  pending: () => ['onboarding-requests', 'pending'] as const,
};

const NOTE_KEYS = createQueryKeys('notes');

const REVIEW_HOLD_KEYS = createQueryKeys('reviewHolds');

const OVERVIEW_KEYS = createQueryKeys('pipelineOverview');

const WAITLIST_KEYS = createQueryKeys('waitlist');

const ACTIVATION_KEYS = createQueryKeys('activation');

const CONCIERGE_KEYS = {
  ...createQueryKeys('concierges'),
  book: (conciergeID: string) => ['concierges', 'book', conciergeID] as const,
};

const CHAT_MONITOR_KEYS = {
  ...createQueryKeys('chatMonitor'),
  threads: (conciergeID: string) => ['chatMonitor', 'threads', conciergeID] as const,
  messages: (channelID: string) => ['chatMonitor', 'messages', channelID] as const,
};

const FINAL_APPROVAL_KEYS = {
  ...createQueryKeys('finalApproval'),
  anchors: () => ['finalApproval', 'anchors'] as const,
};

const MARKET_KEYS = createQueryKeys('markets');

const COUNTER_KEYS = createQueryKeys('counters');

const INTRO_KEYS = createQueryKeys('intros');

const REC_KEYS = {
  ...createQueryKeys('recs'),
  deck: (clientID: string) => ['recs', 'deck', clientID] as const,
  deckByID: (clientID: string, dropID: string | null) => ['recs', 'deck', clientID, dropID] as const,
  nextDrop: (clientID: string) => ['recs', 'next-drop', clientID] as const,
};

const INTERVIEW_KEYS = {
  ...createQueryKeys('interviews'),
  attempts: (applicantID: string) => ['interviews', 'attempts', applicantID] as const,
  conciergeSessions: (memberID: string) => ['interviews', 'concierge-sessions', memberID] as const,
};

const PROMPT_KEYS = createQueryKeys('prompts');

const SCORE_KEYS = {
  ...createQueryKeys('scoring'),
  priority: (applicantID: string) => ['scoring', 'priority', applicantID] as const,
};

const STATUS_TRANSITION_KEYS = createQueryKeys('statusTransitions');

export type {
  TQueryParams,
};
export {
  ACTIVATION_KEYS,
  ACTIVITY_KEYS,
  APPLICANT_KEYS,
  AUDIT_KEYS,
  CALENDLY_KEYS,
  CHAT_KEYS,
  CONSENT_KEYS,
  CHAT_MONITOR_KEYS,
  CONCIERGE_KEYS,
  COUNTER_KEYS,
  FINAL_APPROVAL_KEYS,
  INTERVIEW_KEYS,
  INVITE_KEYS,
  ONBOARDING_REQUEST_KEYS,
  INTRO_KEYS,
  MARKET_KEYS,
  MEMBER_KEYS,
  MODERATION_KEYS,
  NOTE_KEYS,
  OVERVIEW_KEYS,
  PREFERENCE_KEYS,
  PROMPT_KEYS,
  REC_KEYS,
  REVIEW_HOLD_KEYS,
  SAFETY_KEYS,
  SCORE_KEYS,
  SINGLE_KEYS,
  STAFF_INVITE_KEYS,
  STAFF_KEYS,
  STATUS_TRANSITION_KEYS,
  VERIFICATION_KEYS,
  WAITLIST_KEYS,
  createQueryKeys,
};
